import { stateful, useEffect, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { contactMapConsentCopy, OPENSTREETMAP_PRIVACY_URL } from '../../shared/content/contactMapContent.js';
import type { LandingLocale } from '../../shared/content/landingContentTypes.js';
import type { StaticPageContent } from '../../shared/content/staticPageContent.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

export const contactMap = stateful(
  (props: {
    contact: NonNullable<StaticPageContent['contact']>;
    prefix: string;
    locale: LandingLocale;
    scope: string;
    editor?: LandingEditorRenderOptions;
  }) => {
    const { contact, prefix, locale, editor } = props;
    const id = `${prefix}.map`;
    const [granted, setGranted] = useState(false, [contact.mapEmbedUrl, props.scope]);
    const [loaded, setLoaded] = useState(false, [granted, contact.mapEmbedUrl, props.scope]);
    const isEditor = editor?.mode === 'editor';
    const copy = contactMapConsentCopy(locale, contact.mapConsent);
    const controls = useMemo(() => [{ root: createRef<HTMLDivElement>(), focus: false }], []);
    const choose = (value: boolean) => {
      controls.focus = true;
      setGranted(value);
    };
    useEffect(() => {
      if (!controls.focus) return;
      controls.root.value?.querySelector('button')?.focus();
      controls.focus = false;
    }, [granted]);
    const text = (field: keyof typeof copy, tag: 'h3' | 'p' | 'span') =>
      tag === 'span'
        ? html`<span
            class=${editorNodeClass(editor, `${id}.${field}`)}
            style=${editorNodeStyle(editor, `${id}.${field}`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${id}.${field}`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${id}.${field}`, event)}
            >${copy[field]}</span
          >`
        : editorSemanticText({ editor, elementId: `${id}.${field}`, fallbackTag: tag, content: copy[field] });

    // No cookie or localStorage permission: every new view starts with the map blocked.
    useEffect(() => {
      if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
      const revoke = () => setGranted(false);
      window.addEventListener('location-changed', revoke);
      window.addEventListener('popstate', revoke);
      return () => {
        window.removeEventListener('location-changed', revoke);
        window.removeEventListener('popstate', revoke);
      };
    }, []);

    return html`
      <div
        class=${editorNodeClass(editor, id, 'vp-contact-map vp-contact-map-gate')}
        ${ref(controls.root)}
        style=${editorNodeStyle(editor, id)}
        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, id, event)}
        @click=${(event: MouseEvent) => editorNodeClick(editor, id, event)}
      >
        ${granted && !isEditor
          ? html`
              <iframe
                class="vp-contact-map-frame"
                title=${locale === 'tr'
                  ? 'Harita: Castrop-Rauxel’de Viva Porta'
                  : locale === 'en'
                    ? 'Map: Viva Porta in Castrop-Rauxel'
                    : 'Karte: Viva Porta in Castrop-Rauxel'}
                src=${contact.mapEmbedUrl}
                loading="eager"
                @load=${() => setLoaded(true)}
              ></iframe>
              <div class="vp-contact-map-toolbar">
                <div role="status" aria-live="polite">${text(loaded ? 'activeLabel' : 'loadingLabel', 'p')}</div>
                <button type="button" class="vp-action vp-action-secondary" @click=${() => choose(false)}>
                  ${text('unloadLabel', 'span')}
                </button>
              </div>
            `
          : html`
              <div class="vp-contact-map-placeholder">
                <span
                  class="vp-contact-map-icon icon-[material-symbols--location-on-outline-rounded]"
                  aria-hidden="true"
                ></span>
                <div class="vp-contact-map-copy">
                  ${text('title', 'h3')}
                  <p class="vp-contact-map-address">${contact.address}</p>
                  ${text('text', 'p')}
                </div>
                <div class="vp-contact-map-actions">
                  <button
                    type="button"
                    class="vp-action vp-action-primary"
                    @click=${(event: MouseEvent) => {
                      if (isEditor) editorNodeClick(editor, `${id}.loadLabel`, event);
                      else choose(true);
                    }}
                  >
                    ${text('loadLabel', 'span')}
                  </button>
                  <a
                    href=${OPENSTREETMAP_PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="vp-contact-map-privacy"
                  >
                    ${text('privacyLabel', 'span')}
                  </a>
                </div>
              </div>
            `}
      </div>
    `;
  },
);
